<template>
  <div>
    <div class="flex flex-1 max-w-full mx-auto">
      <ToolsToolTitle class="hidden xl:block">
        Project <br >
        Tools
      </ToolsToolTitle>
      <div class="tools flex flex-1 justify-center max-w-full xl:flex-wrap">
        <div class="grid grid-cols-3 gap-4 flex-1 xl:grid-cols-4 xl:gap-2">
          <ToolsToolButton
            icon-class="fa-file-plus"
            label="New"
            :tooltip="TOOLTIP_COPY.New"
            @click="handleNewModal"
          />
          <ToolsToolButton
            icon-class="fa-download"
            label="Import"
            :tooltip="TOOLTIP_COPY.Import"
            @click="modalStore.toggleModal(ModalType.Load)"
          />
          <ToolsToolButton
            icon-class="fa-bookmark"
            label="Save"
            :tooltip="TOOLTIP_COPY.Save"
            @click="handleSave"
          />
          <ToolsToolButton
            icon-class="fad fa-bookmark"
            label="Save As"
            :tooltip="TOOLTIP_COPY['Save As']"
            class="hidden xl:block"
            @click="handleSaveAs"
          />
          <!-- View in Room button positioned to the left of Settings (like Depth button pattern) -->
          <div>
            <div
              class="hidden xl:block"
              style="position: absolute; margin-left: calc(-80px - 0.5rem); width: 80px; height: 50px; z-index: 1000;"
            >
              <ToolsToolButton
                icon-class="fa-cube"
                :label="isExportingAR ? 'Loading...' : 'View in Room'"
                :tooltip="TOOLTIP_COPY['View in Room']"
                :class="{'opacity-75 pointer-events-none': isExportingAR || gridStore.shapeList.length === 0}"
                @click="handleViewInRoom"
              />
            </div>
            <ToolsToolButton
              icon-class="fa-cog"
              label="Settings"
              :tooltip="TOOLTIP_COPY.Settings"
              @click="handleSettings"
            />
          </div>
          <ToolsToolButton
            class="hidden xl:block"
            :class="{'opacity-75 pointer-events-none': !projectStore.designId}"
            icon-class="fa-share-square"
            label="Share"
            :tooltip="TOOLTIP_COPY.Share"
            @click="handleShare"
          />
          <ToolsToolButton
            icon-class="fa-images"
            label="My Designs"
            :tooltip="TOOLTIP_COPY['My Designs']"
            @click="handleMyDesigns"
          />
          <ToolsToolButton
            icon-class="fa-user"
            :label="userStore.isLoggedIn ? 'Logout' : 'Login'"
            :tooltip="userStore.isLoggedIn ? TOOLTIP_COPY.Logout : TOOLTIP_COPY.Login"
            @click="handleLoginModal"
          />
          <!-- View in Room - Mobile only, in hamburger menu -->
          <ToolsToolButton
            class="xl:hidden"
            icon-class="fa-cube"
            :label="isExportingAR ? 'Loading...' : 'View in Room'"
            :tooltip="TOOLTIP_COPY['View in Room']"
            :class="{'opacity-75 pointer-events-none': isExportingAR || gridStore.shapeList.length === 0}"
            @click="handleViewInRoom"
          />
        </div>
      </div>
    </div>

    <!-- View in Room Modal -->
    <ARViewerModal
      :is-open="arModalOpen"
      :model-url="arModelUrl"
      :ar-page-url="arPageUrl"
      :loading="isExportingAR"
      :error="arError"
      @close="closeARModal"
      @retry="handleViewInRoom"
    />
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { ModalType, useModalStore } from '~/stores/modal'
import { useUserStore } from '~/stores/user'
import { useExport3D } from '~/composables/export3D'
import { TOOLTIP_COPY } from '~/composables/useTooltipCopy'

const projectStore = useProjectStore()
const userStore = useUserStore()
const modalStore = useModalStore()
const gridStore = useGridStore()
const { exportToGLB, prepareForAR } = useExport3D()
const { $toast } = useNuxtApp()

const isExportingAR = ref(false)

// View in Room Modal state
const arModalOpen = ref(false)
const arModelUrl = ref(null)
const arPageUrl = ref(null)
const arError = ref(null)

onMounted(() => {
  userStore.initializeFromCookie()
})

const props = defineProps({
  mobile: {
    type: Boolean,
    default: false,
  },
});

const handleNewModal = () => {
  if (props.mobile) {
    modalStore.toggleModal(ModalType.New, { callback: modalStore.toggleModal(ModalType.File) })
  } else {
    modalStore.toggleModal(ModalType.New)
  }
}
const handleLoginModal = () => {
  if (userStore.isLoggedIn) {
    userStore.logout()
  } else {
    modalStore.toggleModal(ModalType.Login)
  }
}
const handleSettings = () => {
  if(!userStore.isLoggedIn) {
    modalStore.toggleModal(ModalType.Login, { callback: handleSettings })
  }
  else{
    modalStore.toggleModal(ModalType.SaveAs, { options: { settings: true } })
  }

}
const handleSave = async () => {
  if(!userStore.isLoggedIn) {
    modalStore.toggleModal(ModalType.Login, { callback: handleSave })
  }
  else if(!projectStore.currentId && !projectStore.designName) {
    modalStore.toggleModal(ModalType.SaveAs, { options: { save: true } })
  } else {
    await projectStore.saveDesign()
  }
}
const handleSaveAs = async () => {
  if (!userStore.isLoggedIn) {
    modalStore.toggleModal(ModalType.Login, { callback: handleSaveAs })
    return
  }
  modalStore.toggleModal(ModalType.SaveAs, {options: {saveAs: true}})
}
const handleMyDesigns = async () => {
  if (!userStore.isLoggedIn) {
    modalStore.toggleModal(ModalType.Login, { callback: handleMyDesigns })
    return
  }
  if (props.mobile) {
    modalStore.toggleModal(ModalType.MyDesigns, { callback: modalStore.toggleModal(ModalType.File) })
  } else{
    modalStore.toggleModal(ModalType.MyDesigns)
  }
}
const handleShare = async () => {
  if(projectStore.designId) {
    modalStore.toggleModal(ModalType.Share)
  }
}

const handleViewInRoom = async () => {
  if (gridStore.shapeList.length === 0) {
    $toast.fire({
      icon: 'warning',
      title: 'Please add tiles to your design first'
    })
    return
  }

  // Show loading state and open modal
  isExportingAR.value = true
  arModalOpen.value = true
  arError.value = null

  const startTime = performance.now()
  console.log('[3D Preview] Starting 3D model generation...', { shapeCount: gridStore.shapeList.length })

  try {
    // Step 1: Export to GLB
    console.log('[3D Preview] Step 1: Exporting to GLB...')
    const exportStart = performance.now()
    const glbData = await exportToGLB()
    console.log(`[3D Preview] Step 1 complete: GLB exported in ${(performance.now() - exportStart).toFixed(0)}ms, size: ${(glbData.byteLength / 1024).toFixed(1)}KB`)

    // Step 2: Upload to get URL
    console.log('[3D Preview] Step 2: Uploading model...')
    const uploadStart = performance.now()
    const { glbUrl } = await prepareForAR(glbData)
    console.log(`[3D Preview] Step 2 complete: Uploaded in ${(performance.now() - uploadStart).toFixed(0)}ms`, { glbUrl })

    // Step 3: Show 3D preview
    arModelUrl.value = glbUrl

    // Generate AR page URL for QR code (desktop users can scan to view in AR on phone)
    const designId = projectStore.designId || projectStore.currentId
    if (designId) {
      let host = window.location.host
      if (host.includes('localhost')) {
        host = '10.69.42.9:3000'
      }
      arPageUrl.value = `https://${host}/?id=${designId}&ar=true`
    }

    console.log(`[3D Preview] Total flow time: ${(performance.now() - startTime).toFixed(0)}ms`)
  } catch (error) {
    console.error('[3D Preview] Failed:', error)
    arError.value = 'Failed to prepare 3D model. Please try again.'
  } finally {
    isExportingAR.value = false
  }
}

const closeARModal = () => {
  arModalOpen.value = false
  arModelUrl.value = null
  arPageUrl.value = null
  arError.value = null
}
</script>
